import {Card, Col, Divider, Form, Row, Skeleton, Space, theme, Typography, Button} from "antd";
import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {CheckCircleFilled, ScheduleOutlined, LinkOutlined, ArrowLeftOutlined} from "@ant-design/icons";
import ScheduledForm from "./ScheduledForm";
import {getMyZapInstance} from "../../../../../services/myZap";
import dayjs from "dayjs";
import {createTask} from "../../../../../services/tasks";
import {getEvolutionInstance} from "../../../../../services/evolution";
import FeegowNotificationTasks from "./FeegowTasks";
import { getFeegowIntegration } from "../../../../../services/feegow-integration";
import { toast } from 'sonner'

const initCards = [
    {
        key: "CRONJOB",
        title: "Agendada",
        description: "Realize uma tarefa com tempo agendado.",
        icon: <ScheduleOutlined style={{marginRight: 8}}/>
    }
]

export default function CreateTask({assistantId}) {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const triggerType = Form.useWatch("triggerType", form);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cards, setCards] = useState(initCards);
    const [instances, setInstances] = useState([]);
      const [instanceOptions, setInstanceOptions] = useState([]);

    const {
        token: {colorPrimary},
    } = theme.useToken();

    const activeStyle = {
        style: {
            borderColor: colorPrimary,
        },
        styles: {
            header: {
                borderColor: colorPrimary
            }
        }
    }

    useEffect(() => {
        setLoading(true);
        let promises = Promise.all([
            getMyZapInstance({assistantID: assistantId}).then(({data}) => {
                if (data.length === 0)
                    return;
                let instance = {type: "MY_ZAP", uuid: data[0].uuid, id: data[0].id}
                setInstances((prev) => [...prev, instance]);
            }),
            getEvolutionInstance({assistantID: assistantId}).then(({data}) => {
                if (data.length === 0)
                    return;
                let instance = {type: "EVOLUTION", uuid: data[0].uuid, id: data[0].id}
                setInstances((prev) => [...prev, instance]);
            }),
            getFeegowIntegration({assistantID: assistantId}).then(({data}) => {
                if (data && data.length > 0) {
                    setCards((prev) => {
                        const hasFeegow = prev.some(c => c.key === "FEEGOW");
                        if (!hasFeegow) {
                            return [...prev, {
                                key: "FEEGOW",
                                title: "Feegow",
                                description: "Lembretes e notificações automáticas no WhatsApp.",
                                icon: <LinkOutlined style={{marginRight: 8}}/>
                            }];
                        }
                        return prev;
                    });
                }
            })
        ])
        promises.catch((e) => {
            toast.error("Não foi possível carregar instâncias")
            console.log(e)
        })
        promises.finally(() => setLoading(false));
    }, [assistantId]);

    const getInstanceOptions = useCallback(() => {
        const initialGroups = {
            // WHATSAPP: [],
            EVOLUTION: [],
            MY_ZAP: []
        };

        const groupedInstances = instances.reduce((acc, instance) => {
            const {type, uuid, id} = instance;
            if (acc[type]) {
                acc[type].push({
                    label: <span>{uuid} - {id}</span>,
                    value: id,
                });
            }
            return acc;
        }, initialGroups);

        const formattedOptions = Object.entries(groupedInstances).map(([type, options]) => ({
            label: <span>{type}</span>,
            title: type,
            options,
        }));

        setInstanceOptions(formattedOptions);
    }, [instances]);

    useEffect(() => {
        getInstanceOptions();
    }, [getInstanceOptions]);

    const getTaskFormData = (formData) => {
        const data = {contacts: formData.contacts, messages: formData.messages}
        return data;
    }

    const getCronTime = (time) => {
        const formatedTime = dayjs(time).format("HH:mm");
        const hourMinute = formatedTime.split(':');
        const cron = `${hourMinute[1]} ${hourMinute[0]} * * *`;
        return cron;
    }

    const getIntegrationType = (id) => {
        const type = instances.find(item => item?.id === id)?.type;
        return type;
    }

    const formatData = (formData) => {
        formData.triggerData = getCronTime(formData.dateSchedule);
        formData.data = getTaskFormData(formData);
        formData.integrationType = getIntegrationType(formData.integrationID);

        return formData;
    }

    const handleCreateTask = useCallback(async (formData) => {
        setCreating(true);
        await createTask(assistantId, formData)
            .then(() => {
                toast.success("Tarefa criada!");
                navigate(`/assistants/${assistantId}/tasks`);
            })
            .catch((err) => {
                console.log(err);
                toast.error("Não foi possível criar tarefa");
            }).finally(() => setCreating(false));
    }, [assistantId, navigate])

    const onFinish = () => {
        const formData = form.getFieldsValue(true);
        const formatedFormData = formatData(formData);
        handleCreateTask(formatedFormData);
    }

    const onCancel = () => {
        form.setFieldValue("triggerType", undefined);
    }

    return (
        <>
            <Row align="middle" style={{ marginBottom: 16 }}>
                <Button 
                    icon={<ArrowLeftOutlined />} 
                    onClick={() => navigate(`/assistants/${assistantId}/tasks`)} 
                    style={{ marginRight: 16 }}
                />
                <Typography.Title level={3} style={{ margin: 0 }}>Criar Tarefa</Typography.Title>
            </Row>
            <Divider style={{ marginTop: 0 }} />
            <Space style={{width: "100%"}} direction={'vertical'}>
                <Row gutter={[8, 8]}>
                    {loading && new Array(4).fill().map((item, key) => <Col span={12} key={key}>
                        <Card loading={loading} title={<Skeleton active paragraph={false}/>}>
                            <Skeleton active title={false} size={'small'}>
                                <Card.Meta
                                    title
                                    extra
                                    avatar
                                />
                            </Skeleton>
                        </Card>
                    </Col>)}
                    {!loading && cards.map(({title, description, icon, key}) =>
                        <Col span={10} key={key}>
                            <Card size={'small'}
                                  {...(triggerType === key ? activeStyle : {})}
                                  onClick={() => form.setFieldValue("triggerType", key)}
                                  title={<> {icon} {title}</>}
                                  extra={triggerType === key && <CheckCircleFilled style={{color: colorPrimary}}/>}
                                  hoverable>
                                <Typography.Paragraph type={'secondary'}
                                                      ellipsis={{rows: 2}}>
                                    {description}
                                </Typography.Paragraph>
                            </Card>
                        </Col>
                    )}
                </Row>
                <Divider/>
                <Form form={form}
                                   layout={'vertical'}
                                   onFinish={onFinish}>
                    <Form.Item name={'triggerType'} initialValue={'CRONJOB'} noStyle/>
                    {triggerType === "CRONJOB" && <ScheduledForm form={form}
                                                                 creating={creating}
                                                                 onCancel={onCancel}
                                                                 instances={instanceOptions}/>}
                                                                 
                    {triggerType === "FEEGOW" && (
                        <div style={{ marginTop: 24 }}>
                            <FeegowNotificationTasks assistantId={assistantId} onCancel={onCancel} />
                        </div>
                    )}
                </Form>
            </Space>
        </>
    )
}
